import React, { useState } from 'react';
import { Box, Container, Typography, Button, Paper, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SpeedIcon from '@mui/icons-material/Speed';
import PublicIcon from '@mui/icons-material/Public';
import ShieldIcon from '@mui/icons-material/Shield';
import Chip from '@mui/material/Chip';

const brandBlue = '#0B60B1';

const steps = [
  {
    label: 'SEO Audit',
    icon: <SearchIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We analyze your site\'s current performance and identify opportunities for improvement.'
  },
  {
    label: 'Strategy Development',
    icon: <AnalyticsIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We create a custom SEO strategy tailored to your business goals and target audience.'
  },
  {
    label: 'Implementation',
    icon: <SpeedIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'Our team implements on-page and technical SEO improvements for better visibility.'
  },
  {
    label: 'Monitoring & Growth',
    icon: <TrendingUpIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We continuously monitor performance and optimize for sustainable growth.'
  }
];

const faqs = [
  {
    q: 'How long does it take to see SEO results?',
    a: 'While some improvements can be seen within weeks, significant results typically appear within 3-6 months. SEO is a long-term strategy that builds sustainable organic traffic.'
  },
  {
    q: 'What\'s included in your SEO service?',
    a: 'We provide comprehensive SEO services including technical audits, keyword research, on-page optimization, content strategy, link building, and performance monitoring.'
  },
  {
    q: 'Do you work with specific industries?',
    a: 'We have experience across various industries and can adapt our strategies to meet your specific business needs and market challenges.'
  },
  {
    q: 'How do you measure SEO success?',
    a: 'We track key metrics including organic traffic, keyword rankings, conversion rates, and ROI. You\'ll receive detailed monthly reports with actionable insights.'
  }
];

const services = [
  'Technical SEO',
  'On-Page Optimization',
  'Content Strategy',
  'Keyword Research',
  'Link Building',
  'Local SEO',
  'E-commerce SEO',
  'Mobile Optimization',
  'Performance Monitoring',
  'Competitive Analysis'
];

const seoFactors = [
  {
    icon: <SearchIcon color="primary" sx={{ fontSize: 36 }} />,
    title: 'Technical SEO',
    desc: 'Optimize your site\'s technical foundation for better search engine visibility.'
  },
  {
    icon: <AnalyticsIcon color="secondary" sx={{ fontSize: 36 }} />,
    title: 'Content Strategy',
    desc: 'Create valuable, engaging content that ranks and converts.'
  },
  {
    icon: <SpeedIcon color="info" sx={{ fontSize: 36 }} />,
    title: 'Performance',
    desc: 'Improve site speed and user experience for better rankings.'
  },
  {
    icon: <PublicIcon color="success" sx={{ fontSize: 36 }} />,
    title: 'Local SEO',
    desc: 'Dominate local search results and attract nearby customers.'
  },
  {
    icon: <ShieldIcon color="error" sx={{ fontSize: 36 }} />,
    title: 'Security',
    desc: 'Ensure your site is secure and trusted by search engines.'
  }
];

export default function SEOPage() {
  const [activeStep, setActiveStep] = useState(0);

  const funnelStats = [
    {
      title: 'SEO Audit Impact',
      stat: '300%',
      desc: 'Average increase in organic traffic after implementing our recommendations.'
    },
    {
      title: 'Strategy Development',
      stat: '85%',
      desc: 'Of keywords reach top 10 positions within 6 months of implementation.'
    },
    {
      title: 'Implementation Results',
      stat: '+200%',
      desc: 'Average improvement in conversion rates from organic traffic.'
    },
    {
      title: 'Growth & Monitoring',
      stat: '4x ROI',
      desc: 'Typical return on investment for our SEO services within 12 months.'
    }
  ];

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', position: 'relative' }}>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', bgcolor: brandBlue, color: '#fff', py: { xs: 10, md: 14 }, overflow: 'hidden', minHeight: { md: 480 } }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={12}>
              <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                <Typography variant="h1" fontWeight={900} gutterBottom sx={{ letterSpacing: '-1.5px', mb: 3, fontSize: { xs: 36, md: 54 } }}>
                  SEO That Drives Growth
                </Typography>
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 400, opacity: 0.95, maxWidth: 500, mx: 'auto' }}>
                  Transform your online presence with data-driven SEO strategies that deliver sustainable results and measurable ROI.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="large" 
                    component={Link}
                    to="/contact"
                    sx={{ fontWeight: 800, px: 5, py: 1.5, fontSize: 20, borderRadius: 3, boxShadow: 4, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}
                  >
                    Get Free SEO Audit
                  </Button>
                  <Button variant="outlined" color="inherit" size="large" sx={{ fontWeight: 700, borderColor: '#fff', px: 4, fontSize: 18, borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>
                    View Case Studies
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
          {/* Subtle animated SVG background */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
            <svg width="100%" height="100%" viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
              <path fill="#1976d2" fillOpacity="0.18" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,133.3C840,107,960,85,1080,101.3C1200,117,1320,171,1380,197.3L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" />
            </svg>
          </Box>
        </Container>
      </Box>

      {/* Features/Stepper Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" fontWeight={900} textAlign="center" gutterBottom sx={{ mb: 6, letterSpacing: '-0.5px' }}>
          How We Drive Results
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0, flexWrap: 'wrap', mb: 4 }}>
          {steps.map((step, idx) => (
            <React.Fragment key={step.label}>
              <Box
                onMouseEnter={() => setActiveStep(idx)}
                onFocus={() => setActiveStep(idx)}
                tabIndex={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mx: 1.5,
                  zIndex: 1,
                }}
              >
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: activeStep === idx ? 'primary.main' : '#e3eafc',
                  color: activeStep === idx ? '#fff' : 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                  boxShadow: activeStep === idx ? 6 : 1,
                  border: activeStep === idx ? '2.5px solid #094a8f' : '2.5px solid #e3eafc',
                  transition: 'all 0.2s',
                  mb: 1,
                }}>{step.icon}</Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ textAlign: 'center', fontSize: 16, mb: 0.5 }}>{step.label}</Typography>
              </Box>
              {idx < steps.length - 1 && (
                <Box sx={{ width: 40, height: 4, bgcolor: '#e3eafc', alignSelf: 'center', mx: 0.5, borderRadius: 2 }} />
              )}
            </React.Fragment>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 180 }}>
          <Paper elevation={6} sx={{ p: 5, borderRadius: 4, bgcolor: '#f7fafd', minWidth: 320, maxWidth: 420, textAlign: 'center', boxShadow: 3, transition: 'all 0.3s', mt: 2 }}>
            <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 2 }}>
              {funnelStats[activeStep].title}
            </Typography>
            <Typography variant="h2" fontWeight={900} color="secondary" sx={{ mb: 1, fontSize: { xs: 36, md: 54 } }}>
              {funnelStats[activeStep].stat}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 340, textAlign: 'center', mx: 'auto' }}>
              {funnelStats[activeStep].desc}
            </Typography>
          </Paper>
        </Box>
      </Container>

      {/* Statistics Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {[
            { label: 'Keywords Ranked', value: '10,000+' },
            { label: 'Average Traffic Growth', value: '300%' },
            { label: 'Client ROI', value: '400%' },
            { label: 'Conversion Rate', value: '2.5x' }
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Our SEO Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Comprehensive SEO solutions to improve your search rankings and drive organic growth
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 4 }}>
          {services.map((service, index) => (
            <Chip
              key={service}
              label={service}
              color={index % 2 === 0 ? 'primary' : 'secondary'}
              variant={index % 3 === 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: 16, px: 2, py: 2, height: 'auto', minHeight: 40, borderRadius: 2, boxShadow: 1 }}
            />
          ))}
        </Box>
      </Container>

      {/* SEO Factors Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom color="primary">
          What We Offer
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Our comprehensive SEO services cover every aspect of search engine optimization.
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {seoFactors.map((factor) => (
            <Grid item xs={12} sm={6} md={4} key={factor.title}>
              <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-6px) scale(1.04)', boxShadow: 6 } }}>
                <Box sx={{ mb: 2 }}>{factor.icon}</Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>{factor.title}</Typography>
                <Typography variant="body2" color="text.secondary">{factor.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Stack spacing={3}>
          {faqs.map((faq) => (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
              <Typography variant="h6" fontWeight={700}>{faq.q}</Typography>
              <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
            </Paper>
          ))}
        </Stack>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: 8, textAlign: 'center', mt: 8, position: 'relative' }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <SearchIcon color="primary" sx={{ fontSize: 48, mb: 1, color: '#fff' }} />
            <Box sx={{ bgcolor: '#fff', color: 'primary.main', px: 3, py: 1, borderRadius: 99, fontWeight: 700, fontSize: 18, boxShadow: 2, mb: 2, display: 'inline-block' }}>
              100% Satisfaction Guarantee
            </Box>
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>Ready to Grow Your Traffic?</Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'rgba(255,255,255,0.85)' }}>
            Let our experts optimize your site for better rankings and more traffic.
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large" 
            component={Link}
            to="/contact"
            sx={{ fontWeight: 700, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}
          >
            Start Your SEO Journey
          </Button>
        </Container>
      </Box>
    </Box>
  );
} 